import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { storage } from '@/utils';

interface CustomBeverageStyle {
  id: number;
  name: string;
  description?: string;
}

interface CustomBeverageStyleManagerProps {
  onStyleAdded?: (style: CustomBeverageStyle) => void;
  onStyleUpdated?: (style: CustomBeverageStyle) => void;
  onStyleDeleted?: (styleId: number) => void;
}

export const CustomBeverageStyleManager: React.FC<CustomBeverageStyleManagerProps> = ({
  onStyleAdded,
  onStyleUpdated,
  onStyleDeleted,
}) => {
  const [styles, setStyles] = useState<CustomBeverageStyle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [newStyle, setNewStyle] = useState({ name: '', description: '' });
  const [editStyle, setEditStyle] = useState({ name: '', description: '' });

  // Load custom beverage styles
  useEffect(() => {
    loadStyles();
  }, []);

  const loadStyles = async () => {
    try {
      const token = storage.getToken();
      if (!token) {
        console.error('No authentication token found');
        setIsLoading(false);
        return;
      }

      const response = await fetch('/api/v1/templates/user/beverage-styles', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setStyles(data);
      }
    } catch (error) {
      console.error('Error loading beverage styles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newStyle.name.trim()) return;

    const token = storage.getToken();
    if (!token) {
      console.error('No authentication token found');
      return;
    }

    try {
      const response = await fetch('/api/v1/templates/user/beverage-styles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newStyle.name.trim(),
          description: newStyle.description.trim() || undefined,
        }),
      });

      if (response.ok) {
        const createdStyle = await response.json();
        setStyles([...styles, createdStyle]);
        setNewStyle({ name: '', description: '' });
        setIsAdding(false);
        onStyleAdded?.(createdStyle);
      }
    } catch (error) {
      console.error('Error creating beverage style:', error);
    }
  };

  const handleEdit = async (id: number) => {
    if (!editStyle.name.trim()) return;

    try {
      const response = await fetch(`/api/v1/templates/user/beverage-styles/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          name: editStyle.name.trim(),
          description: editStyle.description.trim() || undefined,
        }),
      });

      if (response.ok) {
        const updatedStyle = await response.json();
        setStyles(styles.map(s => s.id === id ? updatedStyle : s));
        setEditingId(null);
        setEditStyle({ name: '', description: '' });
        onStyleUpdated?.(updatedStyle);
      }
    } catch (error) {
      console.error('Error updating beverage style:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this beverage style?')) return;

    try {
      const response = await fetch(`/api/v1/templates/user/beverage-styles/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        setStyles(styles.filter(s => s.id !== id));
        onStyleDeleted?.(id);
      }
    } catch (error) {
      console.error('Error deleting beverage style:', error);
    }
  };

  const startEdit = (style: CustomBeverageStyle) => {
    setEditingId(style.id);
    setEditStyle({ name: style.name, description: style.description || '' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditStyle({ name: '', description: '' });
  };

  const cancelAdd = () => {
    setIsAdding(false);
    setNewStyle({ name: '', description: '' });
  };

  if (isLoading) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg">
        <div className="animate-pulse">Loading custom beverage styles...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Custom Beverage Styles</h3>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Style
          </button>
        )}
      </div>

      {/* Add new style form */}
      {isAdding && (
        <div className="p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Style Name</label>
              <input
                type="text"
                value={newStyle.name}
                onChange={(e) => setNewStyle({ ...newStyle, name: e.target.value })}
                placeholder="e.g., Tropical IPA, Vanilla Porter"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description (Optional)</label>
              <textarea
                value={newStyle.description}
                onChange={(e) => setNewStyle({ ...newStyle, description: e.target.value })}
                placeholder="Brief description of this beverage style..."
                rows={2}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleAdd}
                disabled={!newStyle.name.trim()}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="h-4 w-4 mr-1" />
                Save
              </button>
              <button
                onClick={cancelAdd}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Existing styles list */}
      <div className="space-y-2">
        {styles.length === 0 ? (
          <p className="text-gray-500 text-sm italic">No custom beverage styles yet. Add one above!</p>
        ) : (
          styles.map((style) => (
            <div key={style.id} className="p-3 bg-white border border-gray-200 rounded-lg">
              {editingId === style.id ? (
                <div className="space-y-3">
                  <div>
                    <input
                      type="text"
                      value={editStyle.name}
                      onChange={(e) => setEditStyle({ ...editStyle, name: e.target.value })}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <textarea
                      value={editStyle.description}
                      onChange={(e) => setEditStyle({ ...editStyle, description: e.target.value })}
                      rows={2}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(style.id)}
                      disabled={!editStyle.name.trim()}
                      className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                    >
                      <Save className="h-3 w-3 mr-1" />
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <X className="h-3 w-3 mr-1" />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{style.name}</h4>
                    {style.description && (
                      <p className="text-sm text-gray-600 mt-1">{style.description}</p>
                    )}
                  </div>
                  <div className="flex space-x-1 ml-4">
                    <button
                      onClick={() => startEdit(style)}
                      className="p-1 text-gray-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(style.id)}
                      className="p-1 text-gray-400 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};